import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";

const POLL_MS = 2000;
const MAX_POLLS = 4;

export default function CheckoutSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const planId = params.get("plan");
    const [state, setState] = useState({ phase: "loading", data: null, err: "" });

    useEffect(() => {
        if (!sessionId) {
            setState({ phase: "error", err: "Missing session id." });
            return;
        }
        let attempts = 0;
        let cancelled = false;

        const tick = async () => {
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setState({ phase: "paid", data });
                    return;
                }
                if (data.status === "expired") {
                    setState({ phase: "expired", data });
                    return;
                }
                attempts += 1;
                if (attempts >= MAX_POLLS) {
                    setState({ phase: "timeout", data });
                    return;
                }
                setTimeout(tick, POLL_MS);
            } catch (e) {
                if (cancelled) return;
                setState({ phase: "error", err: e.response?.data?.detail || e.message });
            }
        };
        tick();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col" data-testid="checkout-success-page">
            <header className="border-b border-[var(--border)]">
                <div className="max-w-4xl mx-auto px-6 py-5">
                    <Link to="/"><Logo size="sm" /></Link>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center p-6">
                <div className="max-w-xl w-full text-center">
                    {state.phase === "loading" && (
                        <div data-testid="checkout-loading">
                            <Loader2 size={36} className="text-[var(--gold)] mx-auto mb-6 animate-spin" />
                            <p className="font-serif text-3xl mb-2">Confirming your payment…</p>
                            <p className="text-sm text-[var(--text-muted)]">This usually takes a couple seconds.</p>
                        </div>
                    )}

                    {state.phase === "paid" && (
                        <div data-testid="checkout-paid">
                            <CheckCircle2 size={48} className="text-[var(--gold-bright)] mx-auto mb-6" />
                            <p className="label-tiny mb-3">/ {planId} plan · activated</p>
                            <h1 className="font-serif text-5xl md:text-6xl mb-4 leading-[1.05]">
                                Welcome to <span className="gold-gradient-text">NDE Fitness</span>.
                            </h1>
                            <p className="text-base text-[var(--text-muted)] max-w-md mx-auto mb-8">
                                Payment received{state.data?.amount_total ? ` — $${(state.data.amount_total / 100).toFixed(2)} ${(state.data.currency || "usd").toUpperCase()}` : ""}.
                                King Seidorb will personally onboard you within 48 hours. Watch your inbox for your portal invite.
                            </p>
                            <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6 text-left mb-8">
                                <p className="label-tiny mb-3">/ What happens next</p>
                                <ol className="space-y-3 text-sm">
                                    <li><span className="text-[var(--gold)] font-mono mr-2">01</span> You'll get an onboarding email within 24h with a short intake form.</li>
                                    <li><span className="text-[var(--gold)] font-mono mr-2">02</span> Your custom program lands in your athlete portal in 48h.</li>
                                    <li><span className="text-[var(--gold)] font-mono mr-2">03</span> Your first weekly check-in is due 7 days from today.</li>
                                </ol>
                            </div>
                            <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="checkout-go-login">
                                Go to athlete portal <ArrowRight size={14} />
                            </Link>
                        </div>
                    )}

                    {(state.phase === "expired" || state.phase === "error") && (
                        <div data-testid="checkout-error">
                            <AlertCircle size={36} className="text-[var(--danger)] mx-auto mb-6" />
                            <p className="font-serif text-3xl mb-2">
                                {state.phase === "expired" ? "Session expired" : "Something went sideways"}
                            </p>
                            <p className="text-sm text-[var(--text-muted)] mb-6">
                                {state.err || "Please head back to the pricing page and try again."}
                            </p>
                            <Link to="/pricing" className="btn-ghost inline-flex items-center gap-2">Back to pricing</Link>
                        </div>
                    )}

                    {state.phase === "timeout" && (
                        <div data-testid="checkout-pending">
                            <CheckCircle2 size={48} className="text-[var(--gold-bright)] mx-auto mb-6" />
                            <p className="label-tiny mb-3">/ {planId || "selected plan"} · payment received</p>
                            <h1 className="font-serif text-5xl md:text-6xl mb-4 leading-[1.05]">
                                Welcome to <span className="gold-gradient-text">NDE Fitness</span>.
                            </h1>
                            <p className="text-base text-[var(--text-muted)] max-w-md mx-auto mb-8">
                                Stripe is finalizing your receipt — this can take a minute.
                                King Seidorb will personally onboard you within 48 hours. Watch your inbox for your portal invite.
                            </p>
                            <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6 text-left mb-8">
                                <p className="label-tiny mb-3">/ What happens next</p>
                                <ol className="space-y-3 text-sm">
                                    <li><span className="text-[var(--gold)] font-mono mr-2">01</span> You'll get an onboarding email within 24h with a short intake form.</li>
                                    <li><span className="text-[var(--gold)] font-mono mr-2">02</span> Your custom program lands in your athlete portal in 48h.</li>
                                    <li><span className="text-[var(--gold)] font-mono mr-2">03</span> Your first weekly check-in is due 7 days from today.</li>
                                </ol>
                            </div>
                            <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="checkout-go-login">
                                Go to athlete portal <ArrowRight size={14} />
                            </Link>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
